import { useEffect, useRef } from 'react';

/**
 * Keep the top of the detail pane in view when the selection changes.
 *
 * Every two-column screen here keeps its list in place and lets the page scroll
 * the pane beside it. So a click can arrive with the page scrolled deep into the
 * record that was open before, and the new pane starting above the fold — the
 * answer to a click belongs where the eye already is.
 *
 * Only when the top has actually gone out of sight. An ordinary click, made with
 * the pane already in view, must not jump the page under the reader.
 *
 * `showing` is anything that changes when the pane's contents change — the same
 * idea as a key. The pane itself is not watched: it changes size while data
 * loads, and reacting to that would move the page while it is being read.
 */
export function useDetailInView(showing: string) {
  const pane = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = pane.current;
    if (element && element.getBoundingClientRect().top < 0) {
      element.scrollIntoView({ block: 'start' });
    }
  }, [showing]);

  return pane;
}
